import dayjs from 'dayjs';

import type {
  BillingV1FeatureDailyUsage,
  BillingV1FeatureEntityUsage,
  BillingV1FeatureUsageDetail,
  OrganizationOverviewResponse,
} from '@cast/types';
import { toNumOrZero } from '@cast/utils';

import {
  isOverviewClusterDiscovered,
  mapOverviewClusterToSummaryNormalized,
  mergeOverviewSections,
} from './mapOrganizationOverview';

const aggregateProvisionedCpuByDay = (
  points: { timestamp?: string; cpuProvisioned?: string }[] = []
): BillingV1FeatureDailyUsage[] => {
  const byDay = new Map<string, number[]>();

  points.forEach((point) => {
    if (!point.timestamp) {
      return;
    }
    // Browser-local bucketing; matches the local-day boundaries in
    // useReportingPlatformUsageDetail so a selected local day is one bucket.
    const day = dayjs(point.timestamp).format('YYYY-MM-DD');
    const values = byDay.get(day) ?? [];
    values.push(toNumOrZero(point.cpuProvisioned));
    byDay.set(day, values);
  });

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, values]) => ({
      day,
      value: values.reduce((sum, v) => sum + v, 0) / values.length,
    }));
};

/**
 * Period-average provisioned vCPU for connected (agent) fleet from overview
 * timeseries — mean of per-day averages. Falls back to agent summary when
 * timeseries is empty. Temporary until billing-window average exists.
 */
export const averageProvisionedCpuFromOverview = (
  overview?: OrganizationOverviewResponse
): number => {
  const dailyUsages = aggregateProvisionedCpuByDay(
    overview?.agent?.resourceTimeseries
  );
  if (dailyUsages.length > 0) {
    return (
      dailyUsages.reduce((sum, day) => sum + (day.value ?? 0), 0) /
      dailyUsages.length
    );
  }
  return toNumOrZero(overview?.agent?.summary?.totalCpuProvisioned);
};

/**
 * Temporary Reporting meter until billing registers `feature=reporting`.
 * Maps cost-report organization overview (provisioned vCPU) into the
 * platform-usage detail shape the charts/tables already consume.
 *
 * Per-cluster daily series are approximated by allocating fleet daily
 * provisioned vCPU by each cluster's share of period snapshot usage.
 */
export const mapOverviewToReportingUsageDetail = (
  overview?: OrganizationOverviewResponse
): BillingV1FeatureUsageDetail => {
  const merged = mergeOverviewSections(overview?.agent, overview?.agentless);
  const dailyUsages = aggregateProvisionedCpuByDay(merged.resourceTimeseries);

  const entities: BillingV1FeatureEntityUsage[] = (overview?.clusters ?? [])
    .filter((item) => !!item.clusterId && !isOverviewClusterDiscovered(item))
    .map((item) => {
      const summary = mapOverviewClusterToSummaryNormalized(item);
      const usage = toNumOrZero(summary?.cpuProvisionedTotal?.toNumber());
      return {
        entityId: item.clusterId,
        entityName: item.clusterName || item.clusterId,
        entityType: 'cluster',
        usage,
      };
    })
    .filter((entity) => (entity.usage ?? 0) > 0)
    .sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0));

  const entityUsageTotal = entities.reduce((sum, e) => sum + (e.usage ?? 0), 0);

  const entitiesWithDaily: BillingV1FeatureEntityUsage[] = entities.map(
    (entity) => {
      const share =
        entityUsageTotal > 0 ? (entity.usage ?? 0) / entityUsageTotal : 0;
      return {
        ...entity,
        dailyUsages: dailyUsages.map((day) => ({
          day: day.day,
          value: (day.value ?? 0) * share,
        })),
      };
    }
  );

  const totalUsage =
    dailyUsages.length > 0
      ? dailyUsages.reduce((sum, d) => sum + (d.value ?? 0), 0) /
        dailyUsages.length
      : entityUsageTotal || toNumOrZero(merged.summary?.totalCpuProvisioned);

  return {
    feature: 'reporting',
    displayName: 'Reporting',
    unit: 'vCPU',
    totalUsage,
    dailyUsages,
    entities: entitiesWithDaily,
  };
};
